/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  useContent,
  useCaeEnforcement,
  useConsumptionsAndEmissionsFromOneGraphWithLoading,
  useCaeMissingLogger,
} from '@oneaudi/feature-app-utils';
import * as React from 'react';
import { UeContainer } from '@oneaudi/falcon-tools';
import type { TrackingService, OneGraphService } from '../feature-service-types';
import type { Logger } from '@feature-hub/logger';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import type { EditorJsonContent, FalconContent } from '../contentMapping/contentMapping';
import { mapContent } from '../contentMapping/contentMapping';
import { hasValidCaeContent } from '../../utils/hasValidCaeContent';
import { APP_ID } from '../../environment';
import type {
  EditorialTeaserComponent,
  EditorialTeaserContent,
  EditorialTeaserServices,
} from '../../types';
import type { TrackingClickEvent } from '../tracking';
import { useTracking } from '../tracking';
import { isDebugMode } from '../utils';
import { Teaser } from './EditorialTeaser';
import { TeaserContainer } from './EditorialTeaserContainer';
import { getThemeName } from '../../alpha-helpers';

export const EditorialTeasers = ({
  components,
  themeColor = 'light',
  localeService,
  trackingService,
  chunkedComponentsSSR,
  oneGraphService,
  loggerService,
  acsResult,
  gfaServiceConfigProvider,
}: EditorialTeaserContent &
  EditorialTeaserServices & {
    chunkedComponentsSSR: any;
  }): React.ReactElement | null => {
  // CAE enforcement
  const { caeDataRequired, isLoading: isAcsLoading } = useCaeEnforcement({
    gfaServiceConfigProvider: (gfaServiceConfigProvider ?? { gfa: {} }) as any,
    acsResult,
  });

  // Collect all CAE IDs from all teasers (max 10 due to Decision 011)
  const allCaeIds = components
    .flatMap((c) => c.consumptionAndEmissions ?? [])
    .filter(Boolean)
    .slice(0, 10);

  const { data: allCaeData, loading } = useConsumptionsAndEmissionsFromOneGraphWithLoading({
    ids: allCaeIds,
    localeService,
    oneGraphService,
    logger: loggerService ?? undefined,
  });

  // Create lookup map: carlineId -> hasCaeData (validates actual content, not just existence)
  const caeDataMap = React.useMemo(() => {
    const map = new Map<string, boolean>();
    allCaeIds.forEach((id, index) => {
      map.set(id, hasValidCaeContent(allCaeData[index]));
    });
    return map;
  }, [allCaeIds, allCaeData]);

  // Determine which teasers should be hidden
  const teaserVisibility = React.useMemo(
    () =>
      components.map((component) => {
        const caeIds = (component.consumptionAndEmissions ?? []).filter(Boolean);
        if (caeIds.length === 0) return { shouldHide: false, hasCaeData: true, caeIds: [] };

        const hasCaeData = caeIds.every((id) => caeDataMap.get(id) === true);
        const shouldHide = isAcsLoading || (caeDataRequired && (loading || !hasCaeData));

        return { shouldHide, hasCaeData, caeIds };
      }),
    [components, caeDataMap, caeDataRequired, loading, isAcsLoading],
  );

  const missingIds = React.useMemo(
    () =>
      teaserVisibility.flatMap(({ shouldHide, caeIds }) =>
        shouldHide ? caeIds.filter((id) => caeDataMap.get(id) !== true) : [],
      ),
    [teaserVisibility, caeDataMap],
  );

  useCaeMissingLogger({
    enabled: caeDataRequired && !loading,
    logger: loggerService,
    market: localeService.countryCode,
    missingIds,
    pageUrl: typeof window !== 'undefined' ? window.location.pathname : undefined,
  });

  // Check if ALL teasers with CAE IDs are hidden
  const teasersWithIds = teaserVisibility.filter(
    (_v, i) => (components[i].consumptionAndEmissions?.length ?? 0) > 0,
  );
  const allTeasersWithIdsHidden =
    teasersWithIds.length > 0 && teasersWithIds.every((visibility) => visibility.shouldHide);

  const chunkedComponents: any = chunkedComponentsSSR || sliceComponentsIntoChunks(components, 3);
  const ref = React.useRef(null);
  const relatedElements = React.useRef(
    components.map((x, i) => ({
      elementName: 'teaser',
      elementValue: `${i} - ${x.heading || (x.text ? `${x.text?.slice(0, 10)}...` : '(no text)')}`,
    })),
  );
  const {
    registerImpressionTracking,
    sendReadyEvent,
    sendClickEvent: _sendClickEvent,
  } = useTracking(trackingService);
  const sendClickEvent = React.useCallback(
    (event: TrackingClickEvent, componentIndex: number) => {
      _sendClickEvent({
        ...event,
        relatedElements: [relatedElements.current[componentIndex]],
      });
    },
    [_sendClickEvent],
  );

  React.useEffect(() => {
    sendReadyEvent();
  }, []);

  React.useEffect(() => {
    if (ref.current)
      registerImpressionTracking(ref.current, {
        relatedElements: relatedElements.current,
      });
  }, [ref]);

  if (allTeasersWithIdsHidden) {
    return null; // Hide entire FA when all teasers with CAE IDs are missing data.
  }

  return (
    <ThemeProvider theme={{ name: getThemeName(themeColor) }}>
      <div ref={ref} data-testid="editorial-teaser">
        <UeContainer propertyPath="components">
          {chunkedComponents?.map((chunk: ChunkedEditorialTeaserComponent[]) => {
            const containerKey = chunk.map((c) => c.originalIndex).join('-');
            return (
              <TeaserContainer
                key={containerKey}
                numberOfComponents={chunk.length}
                data-testid="teaserContainer"
              >
                {chunk?.map((chunkedComponent: ChunkedEditorialTeaserComponent, i: number) => {
                  const teaserKey = [
                    chunkedComponent.originalIndex,
                    chunkedComponent.heading?.replace(/\s+/g, ''),
                  ].join('_');
                  return (
                    <Teaser
                      key={teaserKey}
                      index={i}
                      content={chunkedComponent}
                      numberOfComponents={chunk.length}
                      sendClickEvent={sendClickEvent}
                      localeService={localeService}
                      oneGraphService={oneGraphService}
                      loggerService={loggerService}
                      shouldHide={
                        teaserVisibility[chunkedComponent.originalIndex]?.shouldHide ?? false
                      }
                    />
                  );
                })}
              </TeaserContainer>
            );
          })}
        </UeContainer>
      </div>
    </ThemeProvider>
  );
};

export const EditorialTeaserContentLoader = ({
  initialContent,
  localeService,
  loggerService,
  oneGraphService,
  ssrContent,
  chunkedComponents,
  trackingService,
  acsResult,
  gfaServiceConfigProvider,
}: {
  initialContent?: EditorialTeaserContent;
  ssrContent: EditorialTeaserContent;
  chunkedComponents: ChunkedEditorialTeaserComponent[];
  trackingService?: TrackingService;
  loggerService?: Logger;
  oneGraphService: OneGraphService;
} & EditorialTeaserServices): React.ReactElement | null => {
  let content: EditorJsonContent;
  if (!ssrContent) {
    const rawContent = useContent<EditorJsonContent | FalconContent>() || initialContent;

    content = mapContent(rawContent as EditorJsonContent | FalconContent);
  } else {
    content = ssrContent;
  }

  if (isDebugMode())
    // eslint-disable-next-line no-restricted-syntax
    console.debug(`${APP_ID} ->`, {
      content,
    });

  if (!content) return null;

  return (
    <EditorialTeasers
      {...content}
      localeService={localeService}
      trackingService={trackingService}
      chunkedComponentsSSR={chunkedComponents}
      loggerService={loggerService}
      oneGraphService={oneGraphService}
      acsResult={acsResult}
      gfaServiceConfigProvider={gfaServiceConfigProvider}
    />
  );
};

interface ChunkedEditorialTeaserComponent extends EditorialTeaserComponent {
  // additional prop for remembering the index from the original
  // array of components before chunking it. Only used for
  // tracking purposes.
  originalIndex: number;
}

/**
 * Put teaser components data in a chunked array so that
 * more than 3 columns can be rendered
 */
function sliceComponentsIntoChunks(
  components: EditorialTeaserComponent[],
  chunkSize: number,
): ChunkedEditorialTeaserComponent[][] {
  const chunkedComponents: ChunkedEditorialTeaserComponent[][] = [];
  let chunk: ChunkedEditorialTeaserComponent[] = [];

  if (typeof window !== 'undefined') {
    components.forEach((component, componentIndex) => {
      chunk.push({ ...component, originalIndex: componentIndex });

      if (chunk.length >= chunkSize) {
        chunkedComponents.push(chunk);
        chunk = [];
      }
    });

    if (chunk.length) {
      chunkedComponents.push(chunk);
    }
  }

  return chunkedComponents;
}
